import { Customer } from '@/types/customer-type';
import { Fulfillment } from '@/types/fulfillment-type';
import {
  OrderItemResponse,
  OrderReferenceItem,
  OrderResponse,
  OrderStateHistory,
  PaymentRecord,
} from '@/types/order-type';
import { Box, Grid } from '@mui/material';
import {
  AddressCard,
  CustomerInfoCard,
  OrderStatusTimeline,
  OrderSummary,
  PaymentInfoCard,
  ProductDetailsTable,
} from './index';
import { OrderHeaderActions } from './OrderHeaderActions';
import { OrderReferenceList } from './OrderReferenceList';
import QRInfoCard from './QRInforCard';

interface OrderDetailsProps {
  order: OrderResponse;
  fulfillment: Fulfillment | null;
  customer: Customer | null;
  orderItems: OrderItemResponse[];
  orderReferences: OrderReferenceItem[];
  orderStateHistory: OrderStateHistory[];
  payments: PaymentRecord[];
  paymentMethod: string;
  onDownloadInvoice: () => void;
  onPrintInvoice: () => void;
  onShowReceipt: () => void;
  onConfirmOrder: () => void;
  onCancelOrder: () => void;
  onViewProfile: () => void;
  onRefresh: () => void;
  onReloadOrder: () => void;
  onReadyForDelivery: (order: OrderResponse) => void;
  onDelivered: (order: OrderResponse) => void;
  onMarkAsCOD: () => void;
  onCapturePayment: () => void;
  onRefundOrder: () => void;
  onReturnOrder: () => void;
  onAfterAddItems?: () => void;
  onAssignToUser: () => void;
  onPaymentSettings?: () => void;
}

export function OrderDetails({
  order,
  customer,
  fulfillment,
  orderItems,
  orderReferences,
  orderStateHistory,
  payments,
  paymentMethod,
  onDownloadInvoice,
  onPrintInvoice,
  onShowReceipt,
  onConfirmOrder,
  onCancelOrder,
  onViewProfile,
  onRefresh,
  onReloadOrder,
  onReadyForDelivery,
  onDelivered,
  onMarkAsCOD,
  onCapturePayment,
  onRefundOrder,
  onReturnOrder,
  onAfterAddItems,
  onAssignToUser,
}: OrderDetailsProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      {/* Main Content - Left Column */}
      <Box
        sx={{
          flex: 1,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            mx: 'auto',
            px: { xs: 2, sm: 3, md: 4, lg: 5 },
            py: { xs: 2, sm: 3, md: 3, lg: 4 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 3, sm: 4, md: 4, lg: 5 },
            }}
          >
            {/* Header Actions - Mobile/Tablet: Show at top, Desktop: Hidden (shown in sidebar) */}
            <Box
              sx={{
                display: { xs: 'block', lg: 'none' },
                mb: { xs: 1, sm: 2 },
              }}
            >
              <OrderHeaderActions
                order={order}
                onDownloadInvoice={onDownloadInvoice}
                onPrintInvoice={onPrintInvoice}
                onShowReceipt={onShowReceipt}
                onConfirmOrder={onConfirmOrder}
                onMarkAsCOD={onMarkAsCOD}
                onCapturePayment={onCapturePayment}
                onCancelOrder={onCancelOrder}
                onMarkReadyForDelivery={() => onReadyForDelivery(order)}
                onMarkDelivered={() => onDelivered(order)}
                onRefundOrder={onRefundOrder}
                onReturnOrder={onReturnOrder}
                onAssignToUser={onAssignToUser}
              />
            </Box>

            {/* Product Details and Order Summary Grid */}
            <Grid container spacing={{ xs: 2, sm: 3, md: 3, lg: 4 }}>
              <Grid item xs={12} xl={8}>
                <ProductDetailsTable
                  orderItems={orderItems}
                  order={order}
                  onAfterAddItems={onAfterAddItems}
                  onRefresh={onRefresh}
                />
              </Grid>

              <Grid item xs={12} xl={4}>
                <OrderSummary
                  order={order}
                  orderItems={orderItems}
                  onConfirmOrder={onConfirmOrder}
                  onCancelOrder={onCancelOrder}
                />
              </Grid>
            </Grid>

            <div className='flex flex-col lg:flex-row gap-6'>
              <div className='flex-1'>
                <OrderReferenceList items={orderReferences} />
              </div>
              <div className='flex-1'>
                <OrderStatusTimeline order={order} onRefresh={onRefresh} history={orderStateHistory} />
              </div>
            </div>
          </Box>
        </Box>
      </Box>

      {/* Sidebar - Right Column */}
      <Box
        component="aside"
        sx={{
          width: { xs: '100%', md: '360px', lg: '420px', xl: '480px' },
          bgcolor: 'background.default',
          borderTop: { xs: 1, md: 0 },



        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 3, md: 3, lg: 4 },
            py: { xs: 2, sm: 3, md: 3, lg: 4 },
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 3, sm: 4, md: 4, lg: 5 },
            alignItems: { xs: 'stretch', md: 'stretch' },
            '& > *': {
              width: { xs: '100%', md: '100%' },
            },
          }}
        >
          {/* Header Actions - Desktop: Show in sidebar, Mobile/Tablet: Hidden (shown at top) */}
          <Box
            sx={{
              display: { xs: 'none', lg: 'block' },
            }}
          >
            <OrderHeaderActions
              order={order}
              onDownloadInvoice={onDownloadInvoice}
              onPrintInvoice={onPrintInvoice}
              onShowReceipt={onShowReceipt}
              onConfirmOrder={onConfirmOrder}
              onMarkAsCOD={onMarkAsCOD}
              onCapturePayment={onCapturePayment}
              onCancelOrder={onCancelOrder}
              onMarkReadyForDelivery={() => onReadyForDelivery(order)}
              onMarkDelivered={() => onDelivered(order)}
              onRefundOrder={onRefundOrder}
              onReturnOrder={onReturnOrder}
              onAssignToUser={onAssignToUser}
            />
          </Box>

          {customer
            && <CustomerInfoCard
              customer={customer}
              onViewProfile={onViewProfile}
              onRefresh={onRefresh}
              orderId={order.id}
              shippingAddress={order.shippingAddress!}
              orderStatus={order.status}
            />}

          <QRInfoCard orderId={order.id} onRefesh={onRefresh} />

          <PaymentInfoCard
            order={order}
            payments={payments}
            orderItems={orderItems}
            onCapturePayment={onCapturePayment}
            onMarkCOD={onMarkAsCOD}
          />
          {customer &&
            <AddressCard
              customer={customer}
              order={order}
              fulfillment={fulfillment}
              title="Shipping Address"
              type="shipping"
              paymentMethod={paymentMethod}
              onReloadOrder={onReloadOrder}
            />}
        </Box>
      </Box>
    </Box>
  );
}
